import { useCallback } from 'react';
import { useNodeStore } from '@/stores/nodeStore';
import { useTaskStore } from '@/stores/taskStore';
import { useAgentStore } from '@/stores/agentStore';
import { eventBus, CanvasEvents } from '@/utils/eventBus';
import { saveAssets } from '@/utils/assetUtils';
import { AppNode, NodeStatus, NodeType } from '@/types';
import type { GenerationParams, MediaGenerateResponse } from '@/types/agent';
import { FEATURE_GRIDS } from '../types';
import { GENERATION_FEATURE_MAP, calculateNewNodePosition } from '../helpers';
import { pendingNodeMap } from '../streamState';

export function useAgentCanvasOps() {
  const addCanvasNode = useNodeStore((s) => s.addNode);
  const updateCanvasNode = useNodeStore((s) => s.updateNode);
  const addCanvasConnection = useNodeStore((s) => s.addConnection);

  const ensureCanvasGenerationNode = useCallback((params: {
    taskType: 'image' | 'video';
    prompt: string;
    modelId?: string;
    currentRefs: Array<{ id: string; node: AppNode }>;
    hasAttachmentInput: boolean;
    generationParams?: GenerationParams;
  }) => {
    const store = useAgentStore.getState();

    // 使用任务参数生成唯一的 taskKey
    const taskKey = `${params.taskType}_${params.prompt.slice(0, 50)}_${params.modelId || 'default'}_${Date.now()}`;

    // 检查是否有相同任务的待处理节点
    if (pendingNodeMap.has(taskKey)) {
      return pendingNodeMap.get(taskKey)!;
    }

    // 仍然检查全局 pendingCanvasNodeId（兼容旧逻辑）
    if (store.pendingCanvasNodeId) {
      // 将旧节点映射到新任务key
      pendingNodeMap.set(taskKey, store.pendingCanvasNodeId);
      return store.pendingCanvasNodeId;
    }

    const isImageTask = params.taskType === 'image';
    const nodeId = `agent-node-${Date.now()}`;
    const shouldUseImageInput = params.currentRefs.length > 0 || params.hasAttachmentInput;
    const gp = params.generationParams;

    const sourceNode = params.currentRefs.length > 0
      ? useNodeStore.getState().getNodeById(params.currentRefs[0].id)
      : null;
    const { x: offsetX, y: offsetY } = calculateNewNodePosition(
      useNodeStore.getState().nodes,
      sourceNode,
    );

    addCanvasNode({
      id: nodeId,
      type: isImageTask ? NodeType.IMAGE_GENERATOR : NodeType.VIDEO_GENERATOR,
      x: offsetX,
      y: offsetY,
      width: 420,
      title: isImageTask ? '智能体图片生成' : '智能体视频生成',
      status: NodeStatus.WORKING,
      data: {
        prompt: params.prompt,
        model: gp?.model || params.modelId || (isImageTask ? 'gemini-2.5-flash-image' : 'veo-3.1-fast-generate-preview'),
        aspectRatio: gp?.aspectRatio || '16:9',
        resolution: gp?.resolution,
        imageCount: gp?.imageCount,
        generationMode: isImageTask
          ? (shouldUseImageInput ? 'IMG2IMG' : 'TXT2IMG')
          : undefined,
        progress: '智能体处理中',
      },
      inputs: [],
    });

    params.currentRefs.forEach((reference) => {
      addCanvasConnection(reference.id, nodeId);
    });

    useAgentStore.getState().setPendingCanvasNodeId(nodeId);
    // 同时添加到 pendingNodeMap，支持多任务并发
    pendingNodeMap.set(taskKey, nodeId);
    eventBus.emit(CanvasEvents.VIEWPORT_FIT);

    return nodeId;
  }, [addCanvasConnection, addCanvasNode]);

  const ensureCanvasGridNode = useCallback((params: {
    feature: string;
    prompt: string;
    modelId?: string;
    currentRefs: Array<{ id: string; node: AppNode }>;
    hasAttachmentInput: boolean;
    generationParams?: GenerationParams;
  }) => {
    const store = useAgentStore.getState();
    if (store.pendingCanvasNodeId) {
      return store.pendingCanvasNodeId;
    }

    const gp = params.generationParams;
    const autoPrompt = GENERATION_FEATURE_MAP[params.feature] || params.prompt;

    if (params.currentRefs.length > 0) {
      const sourceNodeId = params.currentRefs[0].id;
      const sourceNode = useNodeStore.getState().getNodeById(sourceNodeId);

      if (sourceNode) {
        const sourceAspectRatio = sourceNode.data.aspectRatio || '16:9';
        const [srcW, srcH] = sourceAspectRatio.split(':').map(Number);
        const gridConfig = FEATURE_GRIDS[params.feature];
        let targetAspectRatio = sourceAspectRatio;
        if (gridConfig) {
          const overallW = gridConfig.cols * srcW;
          const overallH = gridConfig.rows * srcH;
          const calcGcd = (a: number, b: number): number => b === 0 ? a : calcGcd(b, a % b);
          const g = calcGcd(overallW, overallH);
          targetAspectRatio = `${overallW / g}:${overallH / g}`;
        }

        const { x: newX, y: newY } = calculateNewNodePosition([], sourceNode);
        const newNodeId = `agent-node-${Date.now()}`;

        addCanvasNode({
          id: newNodeId,
          type: NodeType.IMAGE_GENERATOR,
          x: newX,
          y: newY,
          width: 420,
          title: `智能体${params.feature}`,
          status: NodeStatus.WORKING,
          data: {
            prompt: autoPrompt,
            model: gp?.model || params.modelId || 'gemini-2.5-flash-image',
            aspectRatio: targetAspectRatio,
            resolution: gp?.resolution || '2k',
            generationMode: 'IMG2IMG',
            activeFeature: params.feature,
            progress: '智能体处理中',
          },
          inputs: [],
        });

        addCanvasConnection(sourceNodeId, newNodeId);
        useAgentStore.getState().setPendingCanvasNodeId(newNodeId);
        eventBus.emit(CanvasEvents.VIEWPORT_FIT);
        return newNodeId;
      }
    }

    if (params.hasAttachmentInput) {
      const { x: offsetX, y: offsetY } = calculateNewNodePosition(useNodeStore.getState().nodes);

      const uploadNodeId = `agent-upload-${Date.now()}`;
      const genNodeId = `agent-node-${Date.now() + 1}`;

      addCanvasNode({
        id: uploadNodeId,
        type: NodeType.MEDIA_UPLOAD,
        x: offsetX,
        y: offsetY,
        width: 420,
        title: '智能体上传素材',
        status: NodeStatus.SUCCESS,
        data: {
          isManualUpload: true,
          images: [],
        },
        inputs: [],
      });

      addCanvasNode({
        id: genNodeId,
        type: NodeType.IMAGE_GENERATOR,
        x: offsetX + 500,
        y: offsetY,
        width: 420,
        title: `智能体${params.feature}`,
        status: NodeStatus.WORKING,
        data: {
          prompt: autoPrompt,
          model: gp?.model || params.modelId || 'gemini-2.5-flash-image',
          aspectRatio: gp?.aspectRatio || '16:9',
          resolution: gp?.resolution || '2k',
          generationMode: 'IMG2IMG',
          activeFeature: params.feature,
          progress: '智能体处理中',
        },
        inputs: [],
      });

      addCanvasConnection(uploadNodeId, genNodeId);
      useAgentStore.getState().setPendingCanvasNodeId(genNodeId);
      eventBus.emit(CanvasEvents.VIEWPORT_FIT);
      return genNodeId;
    }

    const { x: offsetX, y: offsetY } = calculateNewNodePosition(useNodeStore.getState().nodes);
    const nodeId = `agent-node-${Date.now()}`;

    addCanvasNode({
      id: nodeId,
      type: NodeType.IMAGE_GENERATOR,
      x: offsetX,
      y: offsetY,
      width: 420,
      title: `智能体${params.feature}`,
      status: NodeStatus.WORKING,
      data: {
        prompt: autoPrompt,
        model: gp?.model || params.modelId || 'gemini-2.5-flash-image',
        aspectRatio: gp?.aspectRatio || '16:9',
        resolution: gp?.resolution,
        generationMode: 'TXT2IMG',
        activeFeature: params.feature,
        progress: '智能体处理中',
      },
      inputs: [],
    });

    useAgentStore.getState().setPendingCanvasNodeId(nodeId);
    eventBus.emit(CanvasEvents.VIEWPORT_FIT);
    return nodeId;
  }, [addCanvasConnection, addCanvasNode]);

  const syncCanvasGenerationNodeResult = useCallback(async (nodeId: string, mediaData: MediaGenerateResponse) => {
    const firstResult = mediaData.results[0];
    const isSuccess = mediaData.status === 'SUCCESS';
    const isPending = mediaData.status === 'PENDING' || mediaData.status === 'RUNNING' || mediaData.status === 'QUEUED';
    const nodeStatus = isSuccess ? NodeStatus.SUCCESS : isPending ? NodeStatus.WORKING : NodeStatus.ERROR;

    let imageUrls = mediaData.results.map((result) => result.url).filter(Boolean) as string[];
    let videoUris = imageUrls;

    if (isSuccess && mediaData.results.length > 0) {
      const projectId = useAgentStore.getState().projectId;
      if (projectId) {
        try {
          const isVideo = mediaData.taskType === 'video';
          const { savedUrls } = await saveAssets(projectId, imageUrls, {
            nodeId,
            prefix: `agent_${mediaData.taskType}`,
            metadata: {
              generator_node_id: nodeId,
              generator_type: isVideo ? 'agent_video' : 'agent_image',
              prompt: mediaData.prompt || '',
              model: mediaData.modelId || '',
            },
            isVideo,
          });
          if (isVideo) {
            videoUris = savedUrls;
          } else {
            imageUrls = savedUrls;
          }
        } catch (err) {
          console.error('[智能体] 保存生成资产失败:', err);
        }
      }
    }

    updateCanvasNode(
      nodeId,
      mediaData.taskType === 'image'
        ? {
            prompt: mediaData.prompt,
            model: mediaData.modelId,
            image: imageUrls[0] || firstResult?.url,
            images: imageUrls.length > 0 ? imageUrls : mediaData.results.map((result) => result.url).filter(Boolean),
            imageCount: mediaData.results.length,
            progress: isPending ? '智能体生成中' : undefined,
            error: nodeStatus === NodeStatus.ERROR ? `任务状态：${mediaData.status}` : undefined,
          }
        : {
            prompt: mediaData.prompt,
            model: mediaData.modelId,
            videoUri: videoUris[0] || firstResult?.url,
            videoUris: videoUris.length > 0 ? videoUris : mediaData.results.map((result) => result.url).filter(Boolean),
            videoCount: mediaData.results.length,
            videoMetadata: mediaData.videoMetadata ? {
              ...mediaData.videoMetadata,
              duration: typeof mediaData.videoMetadata.duration === 'number' ? mediaData.videoMetadata.duration : undefined,
            } : undefined,
            duration: typeof mediaData.videoMetadata?.duration === 'number' ? mediaData.videoMetadata.duration : undefined,
            progress: isPending ? '智能体生成中' : undefined,
            error: nodeStatus === NodeStatus.ERROR ? `任务状态：${mediaData.status}` : undefined,
          },
      undefined,
      undefined,
      nodeStatus
    );

    if (!isPending) {
      useAgentStore.getState().setPendingCanvasNodeId(null);
      // 清除 pendingNodeMap 中所有指向该 nodeId 的条目
      for (const [key, value] of pendingNodeMap.entries()) {
        if (value === nodeId) {
          pendingNodeMap.delete(key);
        }
      }
      updateCanvasNode(nodeId, { activeFeature: undefined }, undefined, undefined, undefined);
      const task = useTaskStore.getState().getTaskByNodeId(nodeId);
      if (task) {
        useTaskStore.getState().removeTask(task.taskId);
      }
    }
  }, [updateCanvasNode]);

  return {
    ensureCanvasGenerationNode,
    ensureCanvasGridNode,
    syncCanvasGenerationNodeResult,
  };
}
